import { onMounted, reactive, type Ref } from "vue";
import { defineRule, configure } from "vee-validate";
import { localize, setLocale } from "@vee-validate/i18n";
import { required, max, numeric } from "@vee-validate/rules";
import ja from "@vee-validate/i18n/dist/locale/ja.json";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetAuthType,
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import { getChainList } from "@/common/graphql/chain";
import { getShopList } from "@/common/graphql/shop";
import { checkChainListDisplay } from "@/common/business";
import { getWordList } from "@/common/graphql/expression";
import {
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
  ShopListInput,
  DeliveryTimeListInput,
  DeliveryTimeCommonInput,
  DeliveryTimeDeleteInput,
  DeliveryTimeGetInput,
} from "@/generated/graphql";
import type {
  ChainSelectValue,
  ShopSelectValue,
  AreaListItem,
} from "../types";
import { DELIVERY_SETTING_FILE_NAME } from "../constants";

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseDeliverySettingInitParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  chainListItems: Ref<({ __typename?: "Chain"; chain_code: string; name: string } | null | undefined)[]>;
  chainSelectValues: Ref<ChainSelectValue>;
  shopSelectValues: Ref<ShopSelectValue>;
  shopListItems: Ref<({ __typename?: "ShopResponse"; shop_code: string; name: string } | null | undefined)[]>;
  areaListItems: Ref<AreaListItem[]>;
  shopListInput: ShopListInput;
  deliveryTimeListInput: DeliveryTimeListInput;
  deliveryTimeCommonInput: DeliveryTimeCommonInput;
  deliveryTimeDeleteInput: DeliveryTimeDeleteInput;
  deliveryTimeGetInput: DeliveryTimeGetInput;
  wordList: Record<string, string>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useDeliverySettingInit = ({
  errdialog,
  errMessage,
  chainListItems,
  chainSelectValues,
  shopSelectValues,
  shopListItems,
  areaListItems,
  shopListInput,
  deliveryTimeListInput,
  deliveryTimeCommonInput,
  deliveryTimeDeleteInput,
  deliveryTimeGetInput,
  wordList,
}: UseDeliverySettingInitParams) => {
  // vee-validate ルール定義
  defineRule("required", required);
  defineRule("max", max);
  defineRule("numeric", numeric);
  defineRule("time_format", (value: unknown): true | string => {
    if (value === null || value === undefined || value === "") {
      return true;
    }
    const text = String(value);
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(text)
      || "時刻はhh:mm形式で入力してください。";
  });
  configure({
    generateMessage: localize({
      ja,
    }),
  });
  setLocale("ja");

  // ユーザ権限フラグ
  const chainViewFlag = checkChainListDisplay(localStorageGetAuthType());

  onMounted(async (): Promise<void> => {
    try {
      const apolloClient = await generateApolloClient();
      // 文言データ取得
      Object.assign(wordList, await getWordList(
        localStorageGetChainCode(),
        DELIVERY_SETTING_FILE_NAME,
        localStorageGetCustomerCode()
      ));

      // 運用管理者以外の場合、チェーンコードはログインユーザのチェーンコードにする
      if (chainViewFlag) {
        //チェーン情報を取得する
        chainListItems.value = await getChainList(localStorageGetCustomerCode());
        chainSelectValues.value.chain_code = "";
        shopListInput.chain_code = "";
      } else {
        //チェーン情報を設定する
        const code = localStorageGetChainCode();
        chainSelectValues.value.chain_code = code;
        shopListInput.chain_code = code;
        deliveryTimeListInput.chain_code = code;
        deliveryTimeCommonInput.chain_code = code;
        deliveryTimeDeleteInput.chain_code = code;
        deliveryTimeGetInput.chain_code = code;

        // 店舗リスト取得
        shopListItems.value = await getShopList(shopListInput);

        // エリアデータ取得
        const listArea = await apolloClient.query<ListDeliveryAreaQuery, ListDeliveryAreaQueryVariables>({
          query: ListDeliveryAreaDocument,
          variables: {
            chain_code: code,
            customer_code: localStorageGetCustomerCode(),
            shop_code: shopSelectValues.value.shop_code,
          },
          fetchPolicy: "no-cache",
        });
        areaListItems.value = listArea.data.listDeliveryArea;
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : "エラーが発生しました。";
    }
  });

  return {
    chainViewFlag,
  };
};
